// Frontend kit — shared typed helpers for every page (the JS counterpart of app.css).
// Bundled per-page by esbuild; import what you need, tree-shaking drops the rest.
import type { Severity, StoplightColor, StoplightConfig, Me, Settings } from '../shared/types';
import { SEVERITIES } from '../shared/types';

export type { Severity, StoplightColor, StoplightConfig, Me, Settings };
export { SEVERITIES };

export const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

// A fetch that never swallows: non-2xx throws with the server's message (operator directive).
export async function fetchJSON<T>(url: string, opts?: RequestInit): Promise<T> {
  const r = await fetch(url, opts);
  if (!r.ok) {
    const j = await r.json().catch(() => ({} as any));
    throw new Error(j.error || j.message || `${url} → ${r.status}`);
  }
  return r.json() as Promise<T>;
}

export const postJSON = <T>(url: string, body: unknown) =>
  fetchJSON<T>(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export const escapeHtml = (s: unknown): string =>
  String(s).replace(/[&<>"'`]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' }[c]!));

// Tag → icon (consistent across dashboard + capture). Unknown tags render plain.
export const TAG_ICON: Record<string, string> = {
  Fire: '🔥', Bug: '🐛', Deadline: '📅', Promise: '🤝', Idea: '💡', Distraction: '🌀', "Don't Forget": '📌',
};
export const withTagIcon = (t: string): string => (TAG_ICON[t] ? TAG_ICON[t] + ' ' : '') + t;

// Effort minutes → friendly "~30m" / "~1.5h". '' for missing/zero (never render a bare chip).
export const fmtEffort = (min: number | undefined): string => {
  if (!min || min < 1) return '';
  if (min < 60) return `~${Math.round(min)}m`;
  const h = min / 60;
  return `~${Number.isInteger(h) ? h : h.toFixed(1)}h`;
};

// ── stoplight: per-person severity → color + label (localStorage; server-stored is a later upgrade) ──
export const PALETTE: Record<StoplightColor, { hex: string; rgb: string; name: string }> = {
  green:  { hex: '#37d67a', rgb: '55,214,122',  name: 'Green' },
  red:    { hex: '#ff5c72', rgb: '255,92,114',  name: 'Red' },
  blue:   { hex: '#4aa8ff', rgb: '74,168,255',  name: 'Blue' },
  amber:  { hex: '#f5a524', rgb: '245,165,36',  name: 'Amber' },
  grey:   { hex: '#9aa3c7', rgb: '154,163,199', name: 'Grey' },
  purple: { hex: '#7c5cff', rgb: '124,92,255',  name: 'Purple' },
};

const STOPLIGHT_KEY = 'interrupter.stoplight';
export const STOPLIGHT_DEFAULT: StoplightConfig = {
  drop_everything: { color: 'green', label: 'Now' },
  can_wait:        { color: 'red',   label: 'Later' },
  needs_decision:  { color: 'blue',  label: 'Escalate' },
};

export const Stoplight = {
  get(): StoplightConfig {
    try {
      const raw = localStorage.getItem(STOPLIGHT_KEY);
      if (raw) return { ...STOPLIGHT_DEFAULT, ...(JSON.parse(raw) as Partial<StoplightConfig>) };
    } catch { /* fall through to default */ }
    return { ...STOPLIGHT_DEFAULT };
  },
  set(cfg: StoplightConfig): void { localStorage.setItem(STOPLIGHT_KEY, JSON.stringify(cfg)); },
  // Push the config into CSS vars + return the label map. Call once on page load.
  apply(cfg: StoplightConfig = Stoplight.get(), root: HTMLElement = document.documentElement): Record<Severity, string> {
    const labels = {} as Record<Severity, string>;
    for (const sev of SEVERITIES) {
      const p = PALETTE[cfg[sev].color] || PALETTE.grey;
      root.style.setProperty(`--sev-${sev}`, p.hex);
      root.style.setProperty(`--sev-${sev}-bg`, `rgba(${p.rgb},.15)`);
      labels[sev] = cfg[sev].label;
    }
    return labels;
  },
};
